import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { socialAccountsApi } from '@/lib/api';
import { useAppStore } from '@/stores/appStore';
import { PLATFORMS, PLATFORM_LIST, type Platform } from '@/lib/platforms';
import { PlatformIcon } from '@/components/ui/PlatformIcon';
import { Trash2, Plus, ExternalLink, Loader2, X, AlertCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

const isLinkedInPage = (accountId: string) => accountId.startsWith('urn:li:organization:');

// LinkedIn connects two ways: a person's profile or the company pages they admin.
const CONNECT_OPTIONS = PLATFORM_LIST.flatMap<{ id: string; platform: Platform; label: string }>((p) =>
  p.id === 'linkedin'
    ? [
        { id: 'linkedin', platform: p.id, label: 'LinkedIn Profile' },
        { id: 'linkedin_page', platform: p.id, label: 'LinkedIn Page' },
      ]
    : [{ id: p.id, platform: p.id, label: p.label }]
);

export function SettingsSocialAccounts() {
  const { getToken } = useAuth();
  const { currentWorkspace } = useAppStore();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [connecting, setConnecting] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const selectId = searchParams.get('select');

  const clearParams = () => setSearchParams({}, { replace: true });

  // Handle the post-OAuth redirect: refresh on success, surface any error.
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    const reason = searchParams.get('reason');
    if (connected) {
      qc.invalidateQueries({ queryKey: ['social-accounts', currentWorkspace?.id] });
      clearParams();
    }
    if (error) {
      const label = PLATFORMS[searchParams.get('platform') as keyof typeof PLATFORMS]?.label ?? 'the platform';
      const fallback =
        error === 'no_accounts'
          ? 'No manageable accounts were found for that login. Make sure you grant access to a Page when prompted.'
          : error === 'cancelled'
            ? `Connection to ${label} was cancelled. Nothing was changed — connect again whenever you're ready.`
            : error === 'expired'
              ? `The connection to ${label} took too long and timed out. Please start again.`
              : 'Something went wrong while connecting that account. Please try again.';
      // The server attaches a specific reason (e.g. which permissions were
      // declined) when it can — prefer that over the generic message, unless
      // it's a raw provider response body.
      setErrorMsg(reason && !reason.includes('{') ? reason : fallback);
      clearParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reset the selection whenever a new pending connection appears.
  useEffect(() => {
    setSelectedIds([]);
  }, [selectId]);

  const { data } = useQuery({
    queryKey: ['social-accounts', currentWorkspace?.id],
    queryFn: async () => {
      const token = await getToken();
      return socialAccountsApi.list(token!, currentWorkspace!.id);
    },
    enabled: !!currentWorkspace,
  });

  // Candidate accounts awaiting the user's choice (Meta multi-account grants).
  const {
    data: pending,
    isLoading: pendingLoading,
    isError: pendingError,
  } = useQuery({
    queryKey: ['oauth-pending', selectId],
    queryFn: async () => {
      const token = await getToken();
      return socialAccountsApi.getPendingAccounts(token!, selectId!);
    },
    enabled: !!selectId,
  });

  const deleteAccount = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return socialAccountsApi.delete(token!, id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['social-accounts', currentWorkspace?.id] }),
  });

  const finalize = useMutation({
    mutationFn: async () => {
      const token = await getToken();
      return socialAccountsApi.finalizePending(token!, selectId!, selectedIds);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['social-accounts', currentWorkspace?.id] });
      clearParams();
    },
    onError: (e) => setErrorMsg(e instanceof Error ? e.message : 'Failed to connect account'),
  });

  const connectPlatform = async (platform: string) => {
    if (!currentWorkspace || connecting) return;
    setErrorMsg(null);
    setConnecting(platform);
    try {
      const token = await getToken();
      const { authUrl } = await socialAccountsApi.getOAuthUrl(token!, currentWorkspace.id, platform);
      // Full-page redirect into the provider's consent screen.
      window.location.href = authUrl;
    } catch (e) {
      setConnecting(null);
      setErrorMsg(e instanceof Error ? e.message : 'Failed to start connection');
    }
  };

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const accounts = data?.accounts ?? [];
  const selectPlatform = pending?.platform
    ? PLATFORMS[pending.platform as keyof typeof PLATFORMS]
    : null;

  if (!currentWorkspace) {
    return <p className="text-sm text-muted-foreground">No workspace selected</p>;
  }

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h2 className="text-base font-semibold text-foreground mb-1">Social Accounts</h2>
        <p className="text-xs text-muted-foreground">
          Connect social media accounts to this workspace
        </p>
      </div>

      {errorMsg && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-xs text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="flex-1">{errorMsg}</span>
          <button
            onClick={() => setErrorMsg(null)}
            className="shrink-0 text-destructive/70 hover:text-destructive"
            aria-label="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Connected accounts */}
      {accounts.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Connected
          </h3>
          {accounts.map((account) => {
            const platform = PLATFORMS[account.platform as keyof typeof PLATFORMS];
            return (
              <div
                key={account.id}
                className="flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3"
              >
                <PlatformIcon platform={account.platform} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{account.account_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {isLinkedInPage(account.account_id) ? 'LinkedIn Page' : (platform?.label ?? account.platform)}
                  </p>
                </div>
                <div className="h-2 w-2 rounded-full bg-success shrink-0" />
                <button
                  onClick={() => deleteAccount.mutate(account.id)}
                  className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Available to connect */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Connect Account
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {CONNECT_OPTIONS.map((option) => {
            const connected = accounts.some(
              (a) =>
                a.platform === option.platform &&
                isLinkedInPage(a.account_id) === (option.id === 'linkedin_page')
            );
            const isConnecting = connecting === option.id;
            return (
              <button
                key={option.id}
                onClick={() => connectPlatform(option.id)}
                disabled={!!connecting}
                className="flex items-center gap-3 bg-card border border-border rounded-lg px-3 py-3 hover:border-border/80 transition-colors text-left disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <PlatformIcon platform={option.platform} size="md" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground truncate">{option.label}</p>
                  {isConnecting && <p className="text-xs text-muted-foreground">Connecting…</p>}
                  {!isConnecting && connected && <p className="text-xs text-success">Connected</p>}
                </div>
                {isConnecting ? (
                  <Loader2 className="h-4 w-4 text-muted-foreground shrink-0 animate-spin" />
                ) : connected ? (
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                ) : (
                  <Plus className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Account selection (multi-account grants, e.g. Facebook Pages) */}
      {selectId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                {pending?.platform && <PlatformIcon platform={pending.platform} size="sm" />}
                Choose {selectPlatform?.label ?? 'an'} account
              </h3>
              <button
                onClick={clearParams}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="px-4 py-3 max-h-80 overflow-y-auto">
              {pendingLoading && (
                <div className="flex items-center justify-center py-6 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}

              {pendingError && (
                <p className="py-4 text-xs text-muted-foreground">
                  This connection request has expired. Please start again.
                </p>
              )}

              {!pendingLoading && !pendingError && (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    Select the account(s) you want to connect to this workspace.
                  </p>
                  <div className="space-y-1.5">
                    {pending?.accounts.map((acc) => {
                      const checked = selectedIds.includes(acc.accountId);
                      return (
                        <label
                          key={acc.accountId}
                          className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                            checked
                              ? 'border-accent bg-accent/10'
                              : 'border-border hover:border-border/80'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelected(acc.accountId)}
                            className="h-4 w-4 shrink-0 accent-accent"
                          />
                          <span className="relative flex shrink-0">
                            {acc.profileImageUrl ? (
                              <img
                                src={acc.profileImageUrl}
                                alt=""
                                className="h-8 w-8 rounded-full object-cover"
                              />
                            ) : (
                              <span className="h-8 w-8 rounded-full flex items-center justify-center bg-tertiary ring-1 ring-border text-muted-foreground text-xs font-bold">
                                {acc.accountName[0]?.toUpperCase() ?? '?'}
                              </span>
                            )}
                            {pending?.platform && (
                              <span className="absolute -bottom-0.5 -right-0.5 flex rounded-[4px] ring-2 ring-card">
                                <PlatformIcon platform={pending.platform} size="xs" />
                              </span>
                            )}
                          </span>
                          <span className="text-xs font-medium text-foreground truncate flex-1">
                            {acc.accountName}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
              <button
                onClick={clearParams}
                className="px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                onClick={() => finalize.mutate()}
                disabled={selectedIds.length === 0 || finalize.isPending || pendingLoading}
                className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground rounded-md text-xs font-medium hover:bg-accent/90 disabled:opacity-50 transition-colors"
              >
                {finalize.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Connect{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
